import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { useUserRoles } from '@anytag/auth';
import { ButtonSubmit, Form, Label } from '@anytag/shared-atoms';
import {
  useToggleOption,
  useAllAvailableCountryOptions,
  useAllAgenciesForSearchOptions,
  useAllAdvertisersForSearchOptions
} from '@anytag/shared-hooks';
import {
  TextField,
  RadiosField,
  SelectorField,
  UploadAgencyLogo,
  AsyncSearchNetsuiteVendorField,
  AsyncSearchPrivateInfluencersField,
  SingleAsyncSearchNetsuiteVendorFieldProps
} from '@anytag/shared-molecules';
import { MIME_ACCEPT } from '@anytag/shared-constants';
import { PartnerFormValues, PartnerFormKeys } from './types';

export const Template = () => {
  const { t } = useTranslation();
  const { isAdmin, isStaff } = useUserRoles();
  const { allAgencyOptions } = useAllAgenciesForSearchOptions();
  const { toggleOptions: influencerCostOptions } = useToggleOption();
  const { allAdvertiserOptions } = useAllAdvertisersForSearchOptions();
  const { allAvailableCountryOptions } = useAllAvailableCountryOptions();
  const {
    watch,
    setValue,
    formState: { errors, isSubmitting }
  } = useFormContext<PartnerFormValues>();

  const values = watch();
  const canSelectNetSuite = isAdmin || isStaff;

  const handleNetsuiteIdChange: SingleAsyncSearchNetsuiteVendorFieldProps<PartnerFormKeys>['onChange'] = (
    name,
    value,
    options
  ) => {
    const option = options?.find((option) => option.value === value);

    setValue(name, value, { shouldValidate: true });
    setValue('countryId', option?.countryId || '');
    setValue('companyUrl', option?.companyUrl || '');
    setValue('phoneNumber', option?.phoneNumber || '');
    setValue('partnerAgencyName', option?.label || '');
  };

  return (
    <>
      <Form.Body className="form-container">
        <Form.SectionTitle label={t('Partner Agency Information')} />

        {canSelectNetSuite ? (
          <AsyncSearchNetsuiteVendorField<PartnerFormKeys> name="netsuiteId" onChange={handleNetsuiteIdChange} />
        ) : null}

        <TextField<PartnerFormKeys>
          name="partnerAgencyName"
          title={t('TextForm.Partner Agency Name')}
          placeholder={t<string>('TextForm.AdAsiaLtd')}
          required
        />

        <SelectorField<PartnerFormKeys>
          title={t('Selector.Agency')}
          name="agencyIds"
          help={t('Tooltip.User Agency')}
          options={allAgencyOptions}
          multiple
        />

        <SelectorField<PartnerFormKeys>
          title={t('Selector.Advertiser')}
          name="advertiserIds"
          options={allAdvertiserOptions}
          multiple
        />

        <TextField<PartnerFormKeys>
          name="companyUrl"
          title={t('TextForm.Partner Agency URL')}
          placeholder="https://app.anytag.jp"
        />

        <Form.Fields itemsPerRow={2}>
          <TextField<PartnerFormKeys>
            name="phoneNumber"
            title={t('TextForm.Phone Number')}
            help={t('Tooltip.Phone Number')}
            placeholder="+66020485707"
          />

          <SelectorField<PartnerFormKeys>
            title={t('Selector.Country')}
            name="countryId"
            options={allAvailableCountryOptions}
            required
          />
        </Form.Fields>

        <AsyncSearchPrivateInfluencersField<PartnerFormKeys>
          agencyType="partner"
          disabled={!isAdmin}
          agencyId={Number(values.id)}
          name="privateInfluencerIds"
          placeholder={t<string>('Influencer name')}
          multiple
        />

        <Form.Fields itemsPerRow={2}>
          <Form.Field css={styles.logoWrapper}>
            <Label title={t('Partner Logo')} help={t('Tooltip.customLogoHelp')} />
            <UploadAgencyLogo
              name="logo"
              multiple={false}
              hasError={!!errors.logo}
              value={values.logo ? [values.logo] : []}
              accept={{ ...MIME_ACCEPT.IMAGE, ...MIME_ACCEPT.GIF }}
              onChange={(_, value) => setValue('logo', value[0] || '')}
              notes={[
                t('General.DragAndDropInfo'),
                t('General.DragAndDropMaxSize', { maxSize: '10MB' }),
                t('General.DragAndDropMIME', { MIME: 'PNG, GIF, JPEG' }),
                t('General.DragAndDropImageRatio', { ratio: '16:9' })
              ]}
            />
          </Form.Field>
        </Form.Fields>

        {isAdmin ? (
          <>
            <Form.SectionTitle label={t('Access Permission')} className="mt-10" />

            <RadiosField<PartnerFormKeys>
              name="influencerCostPermission"
              options={influencerCostOptions}
              title={t('Influencer Cost and Profit')}
            />
          </>
        ) : null}
      </Form.Body>

      <Form.Footer>
        <ButtonSubmit loading={isSubmitting} title={t('Button.Save')} disabled={isSubmitting || isStaff} />
      </Form.Footer>
    </>
  );
};

const styles = {
  // TODO: migrate to TW
  logoWrapper: css({
    '& > div > div:first-of-type': {
      border: '1px dashed #e0e8ed',
      '> div > i': { margin: 0, border: 'none', padding: '8px 24px 24px' }
    }
  })
};
